/**
 * Configuration Import/Export Module
 * Spec §5: Configuration format (NORMATIVE)
 *
 * Handles:
 * - Exporting store state to spec-compliant JSON
 * - Importing and validating JSON configurations
 * - Error handling and validation
 */

import type { KeyboardConfig, ValidationResult, ConfigVersion, KeyOverride, LEDOverride, LEDState, ScaleType, SliderConfig } from '@/types/config';
import type { ConfigStore } from '@/store/configStore';

/**
 * Export current configuration to spec §5 format
 * Spec §5.1: Main structure
 *
 * @param store - Current configuration store state
 * @returns Complete keyboard configuration
 */
export function exportConfiguration(store: Partial<ConfigStore>): KeyboardConfig {
  // Spec §5.17: Generate ISO 8601 timestamps
  const now = new Date().toISOString();

  // Spec §5: Complete configuration structure (NORMATIVE)
  const config: KeyboardConfig = {
    // Spec §5.1: Version must match spec version
    config_version: '2.0.0' as ConfigVersion,

    // Spec §5.2: Keyboard grid configuration
    keyboard: {
      modules: {
        columns: store.columns ?? 1,
        rows: store.rows ?? 1,
        ...(store.keysPerModule && store.keysPerModule !== 16 && {
          keys_per_module: store.keysPerModule,
        }),
      },
    },

    // Spec §5.3: Note mapping configuration
    // Default: Wicki-Hayden (whole tones horizontal, octaves vertical)
    note_mapping: {
      origin: store.noteMapping?.origin ?? { q: 0, r: 0 },
      base_note: store.noteMapping?.base_note ?? 60,
      intervals: {
        q: store.noteMapping?.intervals.q ?? 1,  // Wicki-Hayden: Δq=2 → +2 semitones
        r: store.noteMapping?.intervals.r ?? 12, // Wicki-Hayden: Δr=1 → +12 semitones
      },
      // v2.0 cent-based fields (optional, present when tuning enabled)
      ...(store.noteMapping?.cents_mode && {
        cents_mode: true,
        interval_q_cents: store.noteMapping.interval_q_cents,
        interval_r_cents: store.noteMapping.interval_r_cents,
        pitch_bend_range: store.noteMapping.pitch_bend_range,
      }),
    },

    // Spec §5.4: MIDI settings
    midi: {
      default_channel: 0, // Default to channel 0
    },

    // Spec §5.10 + §5.11: LED settings combined
    leds: {
      enabled: store.ledSettings?.enabled ?? true,
      brightness: store.ledSettings?.brightness ?? 0.5,
      ...(store.ledSettings?.perceptual_profile && {
        perceptual_profile: store.ledSettings.perceptual_profile,
      }),
      update_rate_hz: store.ledSettings?.update_rate_hz ?? 120,
      fade_default_ms: store.ledSettings?.fade_default_ms ?? 50,
      default: {
        idle: {
          color: store.ledDefaultBehavior?.idle.color ?? [150, 150, 150],
          brightness: store.ledDefaultBehavior?.idle.brightness ?? 0.5,
        },
        pressed: {
          color: store.ledDefaultBehavior?.pressed.color ?? [255, 255, 255],
          brightness: store.ledDefaultBehavior?.pressed.brightness ?? 1.0,
          fade_ms: store.ledDefaultBehavior?.pressed.fade_ms ?? 0,
        },
      },
    },

    // Spec §5.5: Layers (with optional per-layer note mapping)
    layers: (store.layers ?? [{ id: 0, name: 'default', default: true }]).map((layer) => ({
      id: layer.id,
      name: layer.name,
      ...(layer.default && { default: true }),
      ...(layer.note_mapping && { note_mapping: layer.note_mapping }),
    })),

    // v2.0: Tuning section (optional, present when microtonal enabled)
    ...(store.tuningEnabled && store.tuningTable && {
      tuning: {
        enabled: true,
        table: {
          notes_per_period: store.tuningTable.notes_per_period,
          period_is_octave: store.tuningTable.period_is_octave,
          period_cents: store.tuningTable.period_cents,
          pitches: store.tuningTable.pitches,
          scale_mask: store.tuningTable.scale_mask,
        },
      },
    }),

    // Spec §5.6: Zones (optional)
    ...(store.zones && store.zones.length > 0 && {
      zones: store.zones.map((zone) => ({
        name: zone.name,
        bounds: {
          q_min: zone.bounds.q_min,
          q_max: zone.bounds.q_max,
          ...(zone.bounds.r_min !== undefined && { r_min: zone.bounds.r_min }),
          ...(zone.bounds.r_max !== undefined && { r_max: zone.bounds.r_max }),
        },
        channel: zone.channel,
        transpose: zone.transpose,
        ...(zone.transpose_cents !== undefined && { transpose_cents: zone.transpose_cents }),
        ...(zone.note_mapping && {
          note_mapping: {
            origin: zone.note_mapping.origin,
            base_note: zone.note_mapping.base_note,
            intervals: zone.note_mapping.intervals,
            ...(zone.note_mapping.cents_mode !== undefined && { cents_mode: zone.note_mapping.cents_mode }),
            ...(zone.note_mapping.interval_q_cents !== undefined && { interval_q_cents: zone.note_mapping.interval_q_cents }),
            ...(zone.note_mapping.interval_r_cents !== undefined && { interval_r_cents: zone.note_mapping.interval_r_cents }),
            ...(zone.note_mapping.pitch_bend_range !== undefined && { pitch_bend_range: zone.note_mapping.pitch_bend_range }),
          },
        }),
      })),
    }),

    // Spec §5.7: Key MIDI overrides
    overrides: store.overrides ?? [],

    // Spec §5.12: LED overrides per key
    led_overrides: store.ledOverrides ?? [],

    // Spec §5.13: LED layers (optional)
    ...(store.ledLayers && store.ledLayers.length > 0 && {
      led_layers: store.ledLayers.map((layer) => ({
        layer: layer.layer,
        base_color: layer.base_color,
        brightness: layer.brightness,
        mode: layer.mode,
      })),
    }),

    // Spec §5.14: Scale coloring (optional)
    ...(store.scaleColoring?.enabled && {
      led_scale: {
        enabled: true,
        root_note: store.scaleColoring.root_note,
        ...(store.scaleColoring.root_degree !== undefined && {
          root_degree: store.scaleColoring.root_degree,
        }),
        scale_type: store.scaleColoring.scale_type,
        colors: {
          root: store.scaleColoring.colors.root,
          in_scale: store.scaleColoring.colors.in_scale,
          non_scale: store.scaleColoring.colors.non_scale,
        },
        ...(store.scaleColoring.color_mode === 'per_degree' && store.scaleColoring.degree_colors && {
          color_mode: 'per_degree',
          degree_colors: store.scaleColoring.degree_colors,
        }),
      },
    }),

    // Spec §5.17: Metadata
    metadata: {
      name: store.configName?.trim() || 'Exported Configuration',
      created: now,
      modified: now,
      description: 'Configuration exported from web configurator',
      tags: ['web-export'],
    },
  };

  return config;
}

/**
 * Validate configuration structure and values
 * Spec §5: All validation rules
 *
 * @param config - Configuration to validate
 * @returns Validation result with errors if invalid
 */
export function validateConfiguration(config: unknown): ValidationResult {
  const errors: string[] = [];

  // Type guard - must be an object
  if (typeof config !== 'object' || config === null) {
    return {
      valid: false,
      errors: ['Configuration must be a JSON object'],
    };
  }

  const cfg = config as Record<string, unknown>;

  // Spec §5.1: Version validation (accept 2.0.0 and legacy 1.4.0)
  if (cfg['config_version'] !== '2.0.0' && cfg['config_version'] !== '1.4.0') {
    errors.push(
      `Invalid config_version: expected "2.0.0" or "1.4.0", got "${cfg['config_version']}"`
    );
  }

  // Spec §5.2: Keyboard validation
  if (typeof cfg['keyboard'] !== 'object' || cfg['keyboard'] === null) {
    errors.push('Missing or invalid "keyboard" section');
  } else {
    const kb = cfg['keyboard'] as Record<string, unknown>;
    if (typeof kb['modules'] !== 'object' || kb['modules'] === null) {
      errors.push('Missing or invalid "keyboard.modules"');
    } else {
      const modules = kb['modules'] as Record<string, unknown>;
      const cols = modules['columns'];
      const rows = modules['rows'];

      // Spec §5.2: columns ∈ [1, 8]
      if (typeof cols !== 'number' || cols < 1 || cols > 8) {
        errors.push(`Invalid columns: must be 1-8, got ${cols}`);
      }

      // Spec §5.2: rows ∈ [1, 4]
      if (typeof rows !== 'number' || rows < 1 || rows > 4) {
        errors.push(`Invalid rows: must be 1-4, got ${rows}`);
      }

      // Spec §5.2: columns × rows ≤ 32
      if (
        typeof cols === 'number' &&
        typeof rows === 'number' &&
        cols * rows > 32
      ) {
        errors.push(`Invalid grid: columns × rows must be ≤ 32, got ${cols * rows}`);
      }
    }
  }

  // Spec §5.3: Note mapping validation
  if (typeof cfg['note_mapping'] !== 'object' || cfg['note_mapping'] === null) {
    errors.push('Missing or invalid "note_mapping" section');
  } else {
    const nm = cfg['note_mapping'] as Record<string, unknown>;

    // Validate origin
    if (typeof nm['origin'] !== 'object' || nm['origin'] === null) {
      errors.push('Missing or invalid "note_mapping.origin"');
    } else {
      const origin = nm['origin'] as Record<string, unknown>;
      if (typeof origin['q'] !== 'number' || typeof origin['r'] !== 'number') {
        errors.push('Invalid origin: q and r must be numbers');
      }
    }

    // Spec §5.3: base_note ∈ [0, 127]
    if (typeof nm['base_note'] !== 'number' || nm['base_note'] < 0 || nm['base_note'] > 127) {
      errors.push(`Invalid base_note: must be 0-127, got ${nm['base_note']}`);
    }

    // Validate intervals
    if (typeof nm['intervals'] !== 'object' || nm['intervals'] === null) {
      errors.push('Missing or invalid "note_mapping.intervals"');
    } else {
      const intervals = nm['intervals'] as Record<string, unknown>;

      // Spec §5.3: intervals ∈ [-127, 127]
      if (typeof intervals['q'] !== 'number' || intervals['q'] < -127 || intervals['q'] > 127) {
        errors.push(`Invalid intervals.q: must be -127 to 127, got ${intervals['q']}`);
      }
      if (typeof intervals['r'] !== 'number' || intervals['r'] < -127 || intervals['r'] > 127) {
        errors.push(`Invalid intervals.r: must be -127 to 127, got ${intervals['r']}`);
      }
    }
  }

  // Spec §5.4: MIDI validation
  if (typeof cfg['midi'] !== 'object' || cfg['midi'] === null) {
    errors.push('Missing or invalid "midi" section');
  } else {
    const midi = cfg['midi'] as Record<string, unknown>;

    // Spec §5.4: default_channel ∈ [0, 15]
    if (
      typeof midi['default_channel'] !== 'number' ||
      midi['default_channel'] < 0 ||
      midi['default_channel'] > 15
    ) {
      errors.push(
        `Invalid default_channel: must be 0-15, got ${midi['default_channel']}`
      );
    }
  }

  // Spec §5.5: Layers validation
  if (!Array.isArray(cfg['layers'])) {
    errors.push('Missing or invalid "layers" array');
  } else {
    const layers = cfg['layers'];

    // Spec §5.5: Minimum 1 layer, maximum 16
    if (layers.length === 0) {
      errors.push('At least one layer is required');
    }
    if (layers.length > 16) {
      errors.push(`Too many layers: maximum 16, got ${layers.length}`);
    }

    // Spec §5.5: Exactly one default layer
    const defaultCount = layers.filter((l: unknown) => {
      return typeof l === 'object' && l !== null && (l as Record<string, unknown>)['default'] === true;
    }).length;

    if (defaultCount !== 1) {
      errors.push(`Exactly one layer must have default: true, found ${defaultCount}`);
    }

    // Validate layer IDs are unique and in range
    const ids = new Set<number>();
    layers.forEach((layer: unknown, index: number) => {
      if (typeof layer !== 'object' || layer === null) {
        errors.push(`Layer ${index} is not an object`);
        return;
      }
      const l = layer as Record<string, unknown>;

      if (typeof l['id'] !== 'number' || l['id'] < 0 || l['id'] > 15) {
        errors.push(`Layer ${index}: id must be 0-15, got ${l['id']}`);
      } else {
        if (ids.has(l['id'] as number)) {
          errors.push(`Duplicate layer ID: ${l['id']}`);
        }
        ids.add(l['id'] as number);
      }

      if (typeof l['name'] !== 'string' || l['name'].length === 0 || l['name'].length > 32) {
        errors.push(`Layer ${index}: name must be 1-32 characters`);
      }
    });
  }

  // Spec §5.17: Metadata validation
  if (typeof cfg['metadata'] !== 'object' || cfg['metadata'] === null) {
    errors.push('Missing or invalid "metadata" section');
  } else {
    const meta = cfg['metadata'] as Record<string, unknown>;

    if (typeof meta['name'] !== 'string' || meta['name'].length === 0) {
      errors.push('Metadata must have non-empty name');
    }

    if (typeof meta['created'] !== 'string') {
      errors.push('Metadata must have created timestamp (ISO 8601)');
    }
  }

  // Return validation result
  if (errors.length === 0) {
    return {
      valid: true,
      config: cfg as unknown as KeyboardConfig,
    };
  } else {
    return {
      valid: false,
      errors,
    };
  }
}

/**
 * Download configuration as JSON file
 *
 * @param config - Configuration to download
 * @param filename - Output filename (default: "keyboard-config.json")
 */
export function downloadConfiguration(
  config: KeyboardConfig,
  filename: string = 'keyboard-config.json'
): void {
  // Spec §5: Pretty-print JSON with 2-space indentation
  const json = JSON.stringify(config, null, 2);
  const blob = new Blob([json], { type: 'application/json' });
  const url = URL.createObjectURL(blob);

  // Create temporary download link
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();

  // Cleanup
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Parse and validate imported configuration file
 *
 * @param fileContent - JSON string from uploaded file
 * @returns Validation result
 */
export function parseConfiguration(fileContent: string): ValidationResult {
  try {
    const parsed = JSON.parse(fileContent);
    return validateConfiguration(parsed);
  } catch (error) {
    return {
      valid: false,
      errors: [`JSON parse error: ${error instanceof Error ? error.message : 'Unknown error'}`],
    };
  }
}

/**
 * Parse optional tuning section from JSON config.
 * Returns tuningEnabled + tuningTable fields if present, empty object otherwise.
 */
function parseTuningFromConfig(config: KeyboardConfig): Partial<ConfigStore> {
  if (config.tuning?.enabled && config.tuning.table) {
    return {
      tuningEnabled: true,
      tuningTable: config.tuning.table,
    };
  }
  return {};
}

/**
 * Convert KeyboardConfig (spec format) to ConfigStore format
 * Spec §5: Map all sections to store state
 *
 * @param config - Validated keyboard configuration
 * @returns Store state object
 */
export function configToStore(config: KeyboardConfig): Partial<ConfigStore> {
  return {
    // Spec §5.2: Grid configuration
    columns: config.keyboard.modules.columns,
    rows: config.keyboard.modules.rows,
    keysPerModule: (config.keyboard.modules.keys_per_module === 12 ? 12 : 16) as 12 | 16,

    // Spec §5.3: Note mapping (with optional v2.0 cent fields)
    noteMapping: {
      origin: config.note_mapping.origin,
      base_note: config.note_mapping.base_note,
      intervals: {
        q: config.note_mapping.intervals.q,
        r: config.note_mapping.intervals.r,
      },
      ...(config.note_mapping.cents_mode && {
        cents_mode: true,
        interval_q_cents: config.note_mapping.interval_q_cents,
        interval_r_cents: config.note_mapping.interval_r_cents,
        pitch_bend_range: config.note_mapping.pitch_bend_range,
      }),
    },

    // v2.0: Tuning (optional, parsed from JSON if present)
    ...parseTuningFromConfig(config),

    // Spec §5.10: LED settings
    ledSettings: {
      enabled: config.leds.enabled,
      brightness: config.leds.brightness,
      ...(config.leds.perceptual_profile && {
        perceptual_profile: config.leds.perceptual_profile,
      }),
      update_rate_hz: config.leds.update_rate_hz,
      fade_default_ms: config.leds.fade_default_ms,
    },

    // Spec §5.11: LED default behavior
    ledDefaultBehavior: config.leds.default
      ? {
          idle: {
            color: config.leds.default.idle.color,
            brightness: config.leds.default.idle.brightness,
          },
          pressed: {
            color: config.leds.default.pressed.color,
            brightness: config.leds.default.pressed.brightness,
            fade_ms: config.leds.default.pressed.fade_ms ?? 0,
          },
        }
      : {
          idle: {
            color: [150, 150, 150],
            brightness: 0.5,
          },
          pressed: {
            color: [255, 255, 255],
            brightness: 1.0,
            fade_ms: 0,
          },
        },

    // Spec §5.14: Scale coloring (supports old third/fifth/other and new in_scale/non_scale)
    scaleColoring: config.led_scale
      ? (() => {
          // Work through untyped record for backward compatibility with old JSON format
          // that used third/fifth/other instead of scale_type + in_scale/non_scale
          const raw = config.led_scale as unknown;
          const obj = raw as {
            enabled: boolean;
            root_note: number;
            root_degree?: number;
            scale_type?: ScaleType;
            color_mode?: 'three_color' | 'per_degree';
            degree_colors?: [number, number, number][];
            colors: {
              root: [number, number, number];
              in_scale?: [number, number, number];
              non_scale?: [number, number, number];
              third?: [number, number, number];
              other?: [number, number, number];
            };
          };
          return {
            enabled: obj.enabled,
            // Master firmware only sends root_degree (degree in tuning table), not
            // root_note (MIDI note number). Default root_note to 60 (C4) when missing
            // so the store has a valid number — otherwise UI can't toggle scale on/off.
            root_note: obj.root_note ?? 60,
            ...(obj.root_degree !== undefined && { root_degree: obj.root_degree }),
            scale_type: obj.scale_type ?? ('major' as ScaleType),
            colors: {
              root: obj.colors.root,
              in_scale: obj.colors.in_scale ?? obj.colors.third ?? [0, 100, 255] as [number, number, number],
              non_scale: obj.colors.non_scale ?? obj.colors.other ?? [80, 80, 80] as [number, number, number],
            },
            ...(obj.color_mode === 'per_degree' && obj.degree_colors && {
              color_mode: 'per_degree' as const,
              degree_colors: obj.degree_colors,
            }),
          };
        })()
      : {
          enabled: false,
          root_note: 60,
          scale_type: 'major' as ScaleType,
          colors: {
            root: [255, 0, 0] as [number, number, number],
            in_scale: [0, 100, 255] as [number, number, number],
            non_scale: [80, 80, 80] as [number, number, number],
          },
        },

    // Spec §5.5: Layers (with optional per-layer note mapping + v2.0 cent fields)
    layers: config.layers.map((layer) => ({
      id: layer.id,
      name: layer.name,
      ...(layer.default && { default: true }),
      ...(layer.note_mapping && {
        note_mapping: {
          origin: layer.note_mapping.origin,
          base_note: layer.note_mapping.base_note,
          intervals: {
            q: layer.note_mapping.intervals.q,
            r: layer.note_mapping.intervals.r,
          },
          ...(layer.note_mapping.cents_mode && {
            cents_mode: true,
            interval_q_cents: layer.note_mapping.interval_q_cents,
            interval_r_cents: layer.note_mapping.interval_r_cents,
          }),
        },
      }),
    })),

    // Spec §5.6: Zones (with optional v2.0 transpose_cents)
    zones: config.zones
      ? config.zones.map((zone) => ({
          name: zone.name,
          bounds: {
            q_min: zone.bounds.q_min,
            q_max: zone.bounds.q_max,
            ...(zone.bounds.r_min !== undefined && { r_min: zone.bounds.r_min }),
            ...(zone.bounds.r_max !== undefined && { r_max: zone.bounds.r_max }),
          },
          channel: zone.channel,
          transpose: zone.transpose,
          ...(zone.transpose_cents !== undefined && { transpose_cents: zone.transpose_cents }),
          ...(zone.note_mapping && {
            note_mapping: {
              origin: zone.note_mapping.origin,
              base_note: zone.note_mapping.base_note,
              intervals: zone.note_mapping.intervals,
              ...(zone.note_mapping.cents_mode !== undefined && { cents_mode: zone.note_mapping.cents_mode }),
              ...(zone.note_mapping.interval_q_cents !== undefined && { interval_q_cents: zone.note_mapping.interval_q_cents }),
              ...(zone.note_mapping.interval_r_cents !== undefined && { interval_r_cents: zone.note_mapping.interval_r_cents }),
              ...(zone.note_mapping.pitch_bend_range !== undefined && { pitch_bend_range: zone.note_mapping.pitch_bend_range }),
            },
          }),
        }))
      : [],

    // Spec §5.7: Key MIDI overrides
    // Normalize format: accept both {pos: {q, r}} and flat {q, r}
    overrides: ((config.overrides ?? []) as unknown[]).filter((item) => {
      const override = item as { action?: unknown };
      if (!override.action || typeof override.action !== 'object') {
        console.warn('Skipping override with invalid action:', item);
        return false;
      }
      const action = override.action as { type?: unknown };
      if (typeof action.type !== 'string') {
        console.warn('Skipping override with invalid action type:', item);
        return false;
      }
      return true;
    }).map((item) => {
      const override = item as { pos?: { q: number; r: number }; q?: number; r?: number; layer: number; action: unknown };
      return {
        pos: override.pos ?? { q: override.q ?? 0, r: override.r ?? 0 },
        layer: override.layer,
        action: override.action,
      };
    }) as KeyOverride[],

    // Spec §5.12: LED overrides per key
    // Normalize format: accept both {pos: {q, r}} and flat {q, r}
    ledOverrides: ((config.led_overrides ?? []) as unknown[]).map((item) => {
      const override = item as { pos?: { q: number; r: number }; q?: number; r?: number; layer: number; idle: LEDState; pressed?: LEDState };
      return {
        pos: override.pos ?? { q: override.q ?? 0, r: override.r ?? 0 },
        layer: override.layer,
        idle: override.idle,
        ...(override.pressed && { pressed: override.pressed }),
      };
    }) as LEDOverride[],

    // Spec §5.13: LED layers
    ledLayers: config.led_layers
      ? config.led_layers.map((layer) => ({
          layer: layer.layer,
          base_color: layer.base_color,
          brightness: layer.brightness,
          mode: layer.mode,
        }))
      : [],

    // Spec §5.18: Analog inputs (sliders + pedal)
    // Only set if device actually has analog config — master omits this
    // section when analog_config_present == 0.
    ...(config.analog && {
      analogConfig: {
        sliders: [
          config.analog.sliders[0],
          config.analog.sliders[1],
          config.analog.sliders[2],
        ] as [SliderConfig, SliderConfig, SliderConfig],
        pedal: config.analog.pedal,
      },
    }),

    // v2.1+: Config name from metadata (round-trip preservation)
    // Skip generic "Device Configuration" placeholder from older firmwares.
    ...(config.metadata?.name &&
        config.metadata.name !== 'Device Configuration' && {
      configName: config.metadata.name,
    }),
  };
}
